import { useEffect, useState } from 'react';
import classNames from 'classnames';
import Image from 'next/image';

import { icon_basket } from '@gn-audio/icons';
import { Button, EcomOnly, JabraIcon } from '@gn-audio/jss-components/components';
import { isColorBright } from '@gn-audio/jss-components/utils';
import { HexColor } from '@gn-audio/jss-components/utils/colorUtils';
import { ProductType } from '@gn-audio/jss-core';
import { Option } from '@gn-audio/jss-core/common/models';

import { StandardPriceModel } from 'api/pricesApi/pricesModel';
import { Attribute } from 'api/productCatalogueApi';
import { ColorFilter } from 'composables/Product/Buy/CustomizerFilters/ColorFilter/ColorFilter';
import { Price } from 'composables/Product/Buy/Price/Price';
import useFallbackTranslation from 'utils/hooks/useFallbackTranslation';
import { isNotNil } from 'utils/utils';

export type RelatedProductTile = {
  uniqueSellingPoint: string;
  lifestyleImageAttribute: Attribute | null;
  variants: {
    image: string | null;
    productBuyPageUrl: string | null;
    color: Option;
    bgColor: HexColor;
    productId: number;
  }[];
  productName: string;
  productPageUrl: string | undefined;
  familyId: number;
  colorOptions: Option[];
};

type RelatedProductsItemProps = {
  productTile: RelatedProductTile;
  heroItem: boolean;
  prices: StandardPriceModel[] | null | undefined;
  cardBackgroundColor?: `#${string}`;
};

const COLOR_ANIMATION_STYLES = 'transition-colors ease-linear duration-300';

export const RelatedProductsItem = ({
  productTile,
  heroItem,
  prices,
  cardBackgroundColor,
}: RelatedProductsItemProps) => {
  const { t } = useFallbackTranslation();

  const firstGroup = productTile.variants[0];
  const [currentProductId, setCurrentProductId] = useState<number | undefined>(
    firstGroup?.productId
  );

  useEffect(() => {
    setCurrentProductId(firstGroup.productId);
  }, [firstGroup.productId]);

  const currentGroup =
    productTile.variants.find((group) => group.productId === currentProductId) ?? firstGroup;

  const selectedColorOption = currentGroup.color;
  if (!currentGroup) {
    return null;
  }

  // if prices = undefined (loading) / null (price not available) - pass the state.
  // Otherwise, try to find the price based on the related family, if not present, set to null (price not available)
  const familyPrice = isNotNil(prices)
    ? prices.find((price) => price.familyId === productTile.familyId) || null
    : prices;

  const bgColorHex = cardBackgroundColor || currentGroup.bgColor;
  const isBgBright = !!bgColorHex ? isColorBright(bgColorHex) : true;

  const handleColorChange = (option: Option) => {
    const group = productTile.variants.find((variant) => variant.color?.value === option.value);
    setCurrentProductId(group?.productId);
  };

  const { lifestyleImageAttribute, productName, uniqueSellingPoint, productPageUrl } = productTile;

  const { productBuyPageUrl } = currentGroup;

  return (
    <section
      className={classNames(
        'flex flex-col justify-between overflow-hidden rounded-lg',
        heroItem && 'lg:col-span-2',
        COLOR_ANIMATION_STYLES,
        isBgBright ? 'light' : 'dark'
      )}
      style={{ backgroundColor: bgColorHex }}
      aria-labelledby={`product-${currentProductId}`}
    >
      <div>
        <div className="group relative flex justify-center">
          {lifestyleImageAttribute?.value && (
            <div className="absolute left-0 top-0 h-[300px] opacity-0 transition-all duration-500 ease-linear [clip-path:polygon(0%_0%,0%_0%,0%_100%,0%_100%)] group-hover:opacity-100 group-hover:[clip-path:polygon(0%_0%,100%_0%,100%_100%,0%_100%)]">
              <Image
                src={lifestyleImageAttribute.value}
                height={400}
                width={700}
                className="h-full w-full rounded-r-full object-cover"
                alt={`${productTile.productName} Lifestyle Image`}
              />
            </div>
          )}
          {currentGroup?.image && (
            <Image
              src={`https:${currentGroup?.image}`}
              height={300}
              width={300}
              alt={`${productTile.productName} Image`}
            />
          )}
        </div>
        <div className="mx-6 mt-8">
          {!!productTile.colorOptions?.length && (
            <ColorFilter
              options={productTile.colorOptions}
              onSelect={handleColorChange}
              selected={selectedColorOption}
              dotSize={19}
              showColorTitle={false}
              containerClassName="!gap-1"
            />
          )}
          <h3
            id={`product-${currentProductId}`}
            className={classNames(
              'heading-4 mt-4 text-black dark:text-white',
              COLOR_ANIMATION_STYLES
            )}
          >
            {productName}
          </h3>
          <span
            className={classNames('paragraph', COLOR_ANIMATION_STYLES)}
            dangerouslySetInnerHTML={{
              __html: uniqueSellingPoint,
            }}
          />
        </div>
      </div>
      <div className="mx-6">
        <div className="mt-8 flex justify-end gap-1">
          <Price price={familyPrice} productType={ProductType.Family} />
        </div>
        {(!!productPageUrl || !!productBuyPageUrl) && (
          <div className="mb-10 mt-6 flex flex-wrap justify-end gap-2">
            {productPageUrl && (
              <Button asLink={true} variant="link" size="m" href={productPageUrl}>
                {t('system.LearnMore')}
              </Button>
            )}
            {productBuyPageUrl && (
              <EcomOnly>
                <Button
                  asLink={true}
                  variant="secondary"
                  size="m"
                  href={productBuyPageUrl}
                  icon={<JabraIcon Icon={icon_basket} />}
                  iconPlacement="right"
                  aria-label={`${t('system.Buy')} ${productName}`}
                >
                  {t('system.Buy')}
                </Button>
              </EcomOnly>
            )}
          </div>
        )}
      </div>
    </section>
  );
};
